import React, { useState } from 'react';
import { Button, Modal } from 'antd';
import SunEditor from "suneditor-react";
import ManagePhotoPage from "../../pages/gallery/photo";
import ManageVideoPage from "../../pages/gallery/video";
import ManageDocumentPage from "../../pages/gallery/document";
import "suneditor/dist/css/suneditor.min.css";
import { FORM_LANG, formText } from "../../constants/form-i18n";
import httpUtils from "../../utils/http-utils";
import notifyUtitls from "../../utils/notify-utils";
import { toEditorHtml, toStoredHtml } from "../../utils/content-asset-utils";

const UPLOAD_URL = process.env.REACT_APP_API_ENDPOINT + "/upload/editorImage";

/** Matches an inline base64 image wherever it appears in pasted markup. */
const DATA_URI = /(<img[^>]*\ssrc=["'])data:image\/[^;]+;base64,[^"']*(["'][^>]*>)/gi;

/**
 * NMU-538: how an image gets into the editor, and how it does not.
 *
 * SunEditor with no upload endpoint configured embeds every image as a base64
 * data URI in the HTML, and that is the whole reason the production database is
 * 2.1 GB of which 99% is base64 — 1,469 images inside `content` fields, several
 * documents against Mongo's 16 MB limit, and a site that fell over the moment
 * that data reached uat. `imageFileInput: false` was set here, which removes the
 * file tab from the image dialog and nothing else: paste and drag-drop never
 * went through it.
 *
 * So there are now three ways in and all of them end at a URL:
 *
 *  - the gallery buttons, unchanged, for images already uploaded;
 *  - the editor's own image button and paste/drop, which upload through
 *    {@link uploadImages} and come back as a `/files/embed/...` link;
 *  - markup pasted from elsewhere, whose embedded images are stripped by
 *    {@link stripDataUris} with the editor telling the user what happened.
 *
 * The third one is not paranoia. Pasting from Word or from another CMS carries
 * base64 images inside the HTML itself, where no upload hook ever fires — it is
 * the path that would have quietly kept the problem alive after the other two
 * were closed.
 */
const HtmlEditor = ({ onChange, setContents, showGalleryButtons = true, lang = FORM_LANG.TH, ...props }) => {
	/*
	 * NMU-554: `/files/...` is served by nmu-web, not by this origin.
	 *
	 * Stored markup keeps those URLs root-relative, which is correct — it is what
	 * lets content be copied between environments — but it means the admin's own
	 * browser asks the admin origin for them and gets the SPA's index.html back,
	 * so every inline image in the editor renders as a broken placeholder.
	 *
	 * Rewritten to the public origin for display and back again on the way out,
	 * both here so no dialog has to remember: the editor's value is the document
	 * that gets saved, and rewriting only inbound would write this environment's
	 * hostname into the database.
	 */
	const displayContents = toEditorHtml(setContents);
	const handleChange = (value) => {
		if (onChange) {
			onChange(toStoredHtml(value));
		}
	};

	/** NMU-444: the gallery pickers follow the dialog's active tab language. */
	const t = formText(lang);
	const [showPhotoMaldal, setShowPhotoModal] = useState(false);
	const [showVideoMaldal, setShowVideoModal] = useState(false);
	const [showDocumentMaldal, setShowDocumentModal] = useState(false);

	/**
	 * Uploads through the app's own fetch wrapper rather than SunEditor's
	 * built-in XHR, because that XHR would not carry the Bearer token — the
	 * request would 401 and the editor would report it as a generic failure.
	 *
	 * Returning `undefined` (not `false`) tells SunEditor an upload is in flight
	 * and that it should wait to be called back.
	 *
	 * NMU-597: the callback is found by looking for it, not by its position.
	 * suneditor-react does not pass this the arguments SunEditor documents:
	 * the core calls `onImageUploadBefore(files, info, core, handler)`, and the
	 * React wrapper re-binds it as `(files, info, handler)` — dropping `core`.
	 * Written to the documented four-argument shape, `handler` landed in the
	 * `core` slot and the real parameter was `undefined`, so calling it threw.
	 * The throw happened inside the promise, after the upload had already
	 * succeeded, so nothing ever closed the loading box: the reported spinner
	 * that never stops, with the image sitting uploaded on the server.
	 *
	 * Taking the first function argument works under either shape, so the same
	 * wrapper change cannot cause this a second time.
	 */
	const uploadImages = (...args) => {
		const files = args[0];
		const uploadHandler = args.find((arg) => typeof arg === 'function');

		/*
		 * No way to report anything back, so do not start an upload whose result
		 * could not be delivered — that is what left files on the server with the
		 * editor still spinning. `false` tells SunEditor to stop and close the
		 * loading box, which is the honest outcome.
		 */
		if (typeof uploadHandler !== 'function') {
			notifyUtitls.warning(t('imageUploadFailed'), '');
			return false;
		}

		/*
		 * SunEditor reads this callback's argument by shape, and only two shapes
		 * mean anything: `{result:[…]}` inserts the image, and a plain string is
		 * an error message it shows and closes the spinner for. Anything else —
		 * including the `{errorMessage}` object its own XHR path understands — is
		 * taken as *a list of files to upload*, so a failure reported that way is
		 * silently re-posted rather than shown.
		 */
		const fail = (message) => uploadHandler(String(message || t('imageUploadFailed')));

		const formData = new FormData();
		files.forEach((file, i) => formData.append(`file-${i}`, file));

		httpUtils
			.postFormData(UPLOAD_URL, formData)
			.then((res) => {
				// postFormData resolves to undefined when its interceptor has already
				// surfaced the error.
				if (res && Array.isArray(res.result)) {
					uploadHandler(res);
					return;
				}
				fail(res && res.errorMessage);
			})
			.catch(() => fail())
			/*
			 * The callback itself can throw, and one case is by design: SunEditor's
			 * error path opens its notice and *then* throws, so by the time this
			 * runs the reader has already been told and the loading box is already
			 * closed. Adding a second notification here would report one failure
			 * twice, so this only records it — an unexpected throw still leaves a
			 * trace, without inventing a message for one that was handled.
			 */
			// eslint-disable-next-line no-console
			.catch((err) => console.error('[html-editor] image upload callback threw', err));

		return undefined;
	};

	/**
	 * Drops inline base64 images out of pasted markup, keeping the rest.
	 *
	 * Returning the cleaned string replaces what is pasted. The alternative —
	 * letting them through and rejecting the save — costs the editor their whole
	 * paste with no indication which part was the problem.
	 */
	const stripDataUris = (_event, cleanData) => {
		if (typeof cleanData !== 'string' || !cleanData.includes('data:image/')) {
			return cleanData;
		}
		const stripped = cleanData.replace(DATA_URI, '');
		notifyUtitls.warning(t('imagePasteBlockedTitle'), t('imagePasteBlockedBody'));
		return stripped;
	};

	return (
		<div>
			{showGalleryButtons && (
			<div>
				<Button
					type="primary"
					style={{ marginTop: 5, marginBottom: 5, marginRight: 5 }}
					onClick={() => setShowPhotoModal(true)}
				>
					<i className="feather icon-image" style={{ marginRight: 5 }} /> {t('photoGallery')}
				</Button>
				<Button
					type="primary"
					style={{ marginTop: 5, marginBottom: 5, marginRight: 5 }}
					onClick={() => setShowVideoModal(true)}
				>
					<i className="feather icon-film" style={{ marginRight: 5 }} />{t('videoGallery')}
				</Button>
				<Button
					type="primary"
					style={{ marginTop: 5, marginBottom: 5, marginRight: 5 }}
					onClick={() => setShowDocumentModal(true)}
				>
					<i className="feather icon-file" style={{ marginRight: 5 }} /> {t('documentGallery')}
				</Button>
			</div>
			)}
			<SunEditor
				{...props}
				setOptions={{
					height: 250,
					// NMU-538: the file tab is back on, because there is now somewhere
					// for the file to go. It was off precisely because there was not,
					// which only pushed editors onto paste — the one path that was
					// never blocked and always produced base64.
					imageFileInput: true,
					imageUrlInput: true,
					imageUploadUrl: UPLOAD_URL,
					// 20 MB. Larger than any real page image and small enough that a
					// mistaken upload fails at the browser instead of after the wire.
					imageUploadSizeLimit: 20 * 1024 * 1024,
					imageAccept: '.png,.jpg,.jpeg,.gif',
					buttonList: [
						['undo', 'redo'],
						['font', 'fontSize', 'formatBlock'],
						['paragraphStyle', 'blockquote'],
						['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
						['fontColor', 'hiliteColor', 'textStyle'],
						['removeFormat'],
						['outdent', 'indent'],
						['align', 'horizontalRule', 'list', 'lineHeight'],
						['table', 'link', 'image', 'video', 'audio'],
						['fullScreen', 'showBlocks', 'codeView'],
					],
				}}
				onImageUploadBefore={uploadImages}
				onPaste={stripDataUris}
				setContents={displayContents}
				onChange={handleChange}
			/>

			{/*
			  NMU-454: the pickers are the only thing that can open these, so when
			  they are hidden the dialogs are unreachable — mounting them anyway
			  leaves three gallery pages in the tree that no one can ever open.
			*/}
			{showGalleryButtons && (
			<>
				<Modal
					title="Gallery"
					centered
					visible={showPhotoMaldal}
					onCancel={() => setShowPhotoModal(false)}
					width="1500px"
					destroyOnClose
					footer={null}>
					<ManagePhotoPage isNotModal={false} isHtmlModal={true} />
				</Modal>

				<Modal
					title="Gallery"
					centered
					visible={showVideoMaldal}
					onCancel={() => setShowVideoModal(false)}
					width="1500px"
					destroyOnClose
					footer={null}>
					<ManageVideoPage isNotModal={false} isHtmlModal={true} />
				</Modal>

				<Modal
					title="Gallery"
					centered
					visible={showDocumentMaldal}
					onCancel={() => setShowDocumentModal(false)}
					width="1500px"
					destroyOnClose
					footer={null}>
					<ManageDocumentPage isNotModal={false} isHtmlModal={true} />
				</Modal>
			</>
			)}
		</div>
	);
};

export default HtmlEditor;
